import React, { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';
import { apiService } from '../../services/apiService';
import { FOUNDER_EMAILS, isFounder } from '../../config/founders';
import { IChevronDown, IChevronUp } from './DashIcons';

const WINDOW_DAYS = 30;

const formatRelativeDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Compact uptime formatter for the service-health panel. Backend reports
// seconds since process start; show the largest unit that fits.
const humanizeUptime = (sec) => {
  if (sec == null || Number.isNaN(sec)) return '—';
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
  return `${Math.floor(sec / 86400)}d ${Math.floor((sec % 86400) / 3600)}h`;
};

// One row of the Maintenance panel. Keeps the AdminPage component free
// of repetitive button/description markup.
const MaintRow = ({ title, desc, action, onClick, pending, danger }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      padding: '12px 0',
      borderBottom: '1px solid var(--line)',
    }}
  >
    <div style={{ flex: 1 }}>
      <div style={{ fontWeight: 500, marginBottom: 2 }}>{title}</div>
      <div style={{ fontSize: 12, color: 'var(--ink-4)', lineHeight: 1.5 }}>{desc}</div>
    </div>
    <button
      type="button"
      className={`btn btn-ghost ${danger ? 'btn-danger' : ''}`}
      onClick={onClick}
      disabled={pending}
      style={{ minWidth: 140, justifyContent: 'center' }}
    >
      {pending ? 'Running…' : action}
    </button>
  </div>
);

// The backend may return a flag's current value in a few different shapes
// depending on org vs. global scope. Try each before defaulting to false.
const readFlag = (features, key) => {
  if (!features) return false;
  if (typeof features[key] === 'boolean') return features[key];
  if (features.flags && typeof features.flags[key] === 'boolean') return features.flags[key];
  if (features.features && typeof features.features[key] === 'boolean') return features.features[key];
  return false;
};

const AdminPage = () => {
  const { user } = useAuth();
  const allowed = isFounder(user?.email);
  const queryClient = useQueryClient();
  const [showUsers, setShowUsers] = useState(false);

  // Platform stats — declared unconditionally to keep hook order stable.
  const { data: statsData, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ['platform-stats'],
    queryFn: () => apiService.getPlatformStats(),
    staleTime: 30000,
    refetchInterval: 60000,
    enabled: !!user && allowed,
    retry: false,
  });

  // Feature flags — fetched alongside stats so toggles reflect server state.
  const { data: featuresData } = useQuery({
    queryKey: ['admin-features', user?.orgId],
    queryFn: () => apiService.getAdminFeatures(user?.orgId),
    staleTime: 30000,
    enabled: !!user && allowed,
    retry: false,
  });

  // Lazy user list — only fires when the disclosure is opened.
  const { data: userData, isLoading: usersLoading, error: usersError } = useQuery({
    queryKey: ['platform-users'],
    queryFn: () => apiService.getUserList(),
    staleTime: 30000,
    enabled: !!user && allowed && showUsers,
    retry: false,
  });

  // Live process metrics — uptime, memory, requests/min, goroutines. Drives
  // the SLO panel below. Polled every 15s so admins see live drift.
  const { data: adminMetrics } = useQuery({
    queryKey: ['admin-metrics'],
    queryFn: () => apiService.getAdminMetrics(),
    staleTime: 10000,
    refetchInterval: 15000,
    enabled: !!user && allowed,
    retry: false,
  });

  // IDF stats — top 25 tags by docCount in this org plus the corpus size,
  // smoothing alpha, and gate threshold. Lets admins see exactly what the
  // retriever treats as common (low IDF) vs rare (high IDF) and tune
  // tag-cleaning accordingly.
  const { data: idfStats } = useQuery({
    queryKey: ['admin-idf-stats'],
    queryFn: () => apiService.getAdminIDFStats({ top: 25 }),
    staleTime: 60000,
    enabled: !!user && allowed,
    retry: false,
  });

  // Tagmaker observability — health, queue, DB status. Polled every 20s.
  // Each is a separate query so a single failing endpoint doesn't blank
  // the others (Promise.allSettled-style isolation).
  const { data: tagmakerHealth, error: tagmakerHealthErr } = useQuery({
    queryKey: ['tagmaker-health'],
    queryFn: () => apiService.getTagmakerHealth(),
    staleTime: 15000,
    refetchInterval: 20000,
    enabled: !!user && allowed,
    retry: false,
  });
  const { data: tagmakerQueue, error: tagmakerQueueErr } = useQuery({
    queryKey: ['tagmaker-queue'],
    queryFn: () => apiService.getQueueStatus(),
    staleTime: 15000,
    refetchInterval: 20000,
    enabled: !!user && allowed,
    retry: false,
  });
  const { data: tagmakerDb, error: tagmakerDbErr } = useQuery({
    queryKey: ['tagmaker-db'],
    queryFn: () => apiService.getDatabaseStatus(),
    staleTime: 15000,
    refetchInterval: 20000,
    enabled: !!user && allowed,
    retry: false,
  });

  // Maintenance mutations — each operation is a separate mutation so the
  // last-result toast and error state don't collide. Each shows a toast
  // on success/error and invalidates the cache that would surface the
  // change in the dashboard. Confirmation modals live at the call site.
  const cleanup = useMutation({
    mutationFn: () => apiService.cleanupMemories({ orgId: user?.orgId }),
    onSuccess: (d) => toast.success(`Cleanup ran: ${d?.deleted ?? 0} stale memories removed`),
    onError: (e) => toast.error(e?.response?.data?.error || 'Cleanup failed'),
  });
  const prune = useMutation({
    mutationFn: () => apiService.pruneEdges({ orgId: user?.orgId }),
    onSuccess: (d) => toast.success(`Edge prune ran: ${d?.pruned ?? 0} weak edges removed`),
    onError: (e) => toast.error(e?.response?.data?.error || 'Edge prune failed'),
  });
  const backfill = useMutation({
    mutationFn: () => apiService.backfillTags({ orgId: user?.orgId }),
    onSuccess: (d) => toast.success(`Backfill ran: ${d?.updated ?? 0} memories updated`),
    onError: (e) => toast.error(e?.response?.data?.error || 'Backfill failed'),
  });
  const queueClear = useMutation({
    mutationFn: () => apiService.clearFailedQueue(),
    onSuccess: () => {
      toast.success('Failed-job queue cleared');
      queryClient.invalidateQueries({ queryKey: ['tagmaker-queue'] });
    },
    onError: (e) => toast.error(e?.response?.data?.error || 'Queue clear failed'),
  });
  const queueRetry = useMutation({
    mutationFn: () => apiService.retryFailedQueue(),
    onSuccess: (d) => {
      toast.success(`${d?.retried ?? 0} failed jobs retried`);
      queryClient.invalidateQueries({ queryKey: ['tagmaker-queue'] });
    },
    onError: (e) => toast.error(e?.response?.data?.error || 'Queue retry failed'),
  });
  const rebalance = useMutation({
    mutationFn: () => apiService.forceRebalance(),
    onSuccess: () => toast.success('Tag rebalance triggered'),
    onError: (e) => toast.error(e?.response?.data?.error || 'Rebalance failed'),
  });
  // EMA edge-learning replay over the last N hours of pack events.
  // Touches edge weights in the tag graph, so this is an admin-only
  // operation behind a confirmation. Default window 24 hours.
  const edgeLearn = useMutation({
    mutationFn: () =>
      apiService.triggerEdgeLearningReplay({ orgId: user?.orgId, window_hours: 24 }),
    onSuccess: (d) => {
      if (d?.mockData) {
        toast.error('Edge-learning service is unavailable on this deploy');
        return;
      }
      const events = d?.events_processed ?? d?.eventsProcessed ?? '?';
      const updates = d?.edges_updated ?? d?.edgesUpdated ?? '?';
      toast.success(`Replay processed ${events} events, updated ${updates} edges`);
    },
    onError: (e) => toast.error(e?.response?.data?.error || 'Edge replay failed'),
  });

  // Optimistic toggle — flip the cache, fire the mutation, roll back on error.
  const toggleFeature = useMutation({
    mutationFn: (payload) => apiService.setAdminFeatures(payload),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: ['admin-features', user?.orgId] });
      const previous = queryClient.getQueryData(['admin-features', user?.orgId]);
      queryClient.setQueryData(['admin-features', user?.orgId], (old) => ({
        ...(old || {}),
        ...payload,
      }));
      return { previous };
    },
    onError: (err, _payload, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(['admin-features', user?.orgId], ctx.previous);
      }
      toast.error(err?.response?.data?.error || 'Failed to update feature flag');
    },
    onSuccess: () => {
      toast.success('Feature flag updated');
      queryClient.invalidateQueries({ queryKey: ['admin-features', user?.orgId] });
    },
  });

  const propagationOn = readFlag(featuresData, 'PROPAGATION_ON');
  const sloGuard = readFlag(featuresData, 'SLO_GUARD');

  // 30-day signup sparkline — same monochrome amber look as the home page.
  const signupRows = useMemo(() => statsData?.signups_last_30d || [], [statsData]);
  const series = useMemo(() => {
    const buckets = new Array(WINDOW_DAYS).fill(0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startTs = today.getTime() - (WINDOW_DAYS - 1) * 86400 * 1000;
    signupRows.forEach((r) => {
      if (!r?.date) return;
      const day = new Date(r.date + 'T00:00:00');
      day.setHours(0, 0, 0, 0);
      const idx = Math.floor((day.getTime() - startTs) / 86400000);
      if (idx >= 0 && idx < WINDOW_DAYS) buckets[idx] += r.count || 0;
    });
    return buckets;
  }, [signupRows]);

  const seriesMax = Math.max(1, ...series);
  const w = 640;
  const h = 160;
  const pad = 8;
  const xs = series.map((_, i) => pad + (i / (series.length - 1)) * (w - pad * 2));
  const ys = series.map((v) => h - pad - (v / seriesMax) * (h - pad * 2));
  const linePath = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x},${ys[i]}`).join(' ');
  const fillPath = `${linePath} L${xs[xs.length - 1]},${h} L${xs[0]},${h} Z`;

  const users = userData?.users || [];

  // Tenancy — read from auth user + sessionStorage so the panel mirrors
  // what the backend actually sees when it authorizes a request.
  const tenancy = useMemo(() => {
    const orgId =
      user?.orgId ||
      (typeof window !== 'undefined' ? sessionStorage.getItem('mme_org_id') : '') ||
      '—';
    const userId = user?.userId || user?.id || '—';
    const projectId = user?.projectId || 'default';
    return [
      { k: 'orgId', v: orgId },
      { k: 'userId', v: userId },
      { k: 'projectId', v: projectId },
      { k: 'jwtIssuer', v: 'mme-tagging-service' },
      { k: 'tokenTTL', v: '24h' },
      { k: 'refreshTTL', v: '30d' },
    ];
  }, [user]);

  const windowStart = new Date(Date.now() - (WINDOW_DAYS - 1) * 86400 * 1000);
  const windowMid = new Date(Date.now() - Math.floor((WINDOW_DAYS - 1) / 2) * 86400 * 1000);
  const formatDay = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  // Client-side gate — the backend enforces the same check independently.
  if (!allowed) {
    return <Navigate to="/dashboard" replace />;
  }

  const totalUsers = statsData?.total_users ?? 0;
  const activeUsers = statsData?.active_users ?? 0;
  const googleUsers = statsData?.google_users ?? 0;
  const totalMemories = statsData?.total_memories ?? 0;
  const totalFeedback = statsData?.total_feedback ?? 0;

  return (
    <>
      <Helmet><title>Admin · MME</title></Helmet>
      <div className="page">
        <div className="page-head">
          <span className="badge" style={{ marginBottom: 10 }}>Founder only</span>
          <h1>Platform</h1>
          <div className="sub">
            Signups, feature flags, SLO health, and tenancy — everything ops needs in one place.
          </div>
        </div>

        {statsError && (
          <div className="error-banner">
            {statsError?.response?.status === 403
              ? 'Your account is not in the founder allowlist. Confirm MME_FOUNDER_EMAILS on the backend includes this account.'
              : 'The platform-stats endpoint is unreachable. Check the tagging service is deployed with the latest build.'}
          </div>
        )}

        {/* Platform KPIs — real data from /admin/platform-stats */}
        <div className="kpi-row">
          <div className="kpi">
            <div className="lbl">Total users</div>
            <div className="val">{statsLoading ? '—' : totalUsers.toLocaleString()}</div>
            <div className="delta">{googleUsers.toLocaleString()} via Google OAuth</div>
          </div>
          <div className="kpi">
            <div className="lbl">Active users</div>
            <div className="val">{statsLoading ? '—' : activeUsers.toLocaleString()}</div>
            <div className="delta">keys with active:true</div>
          </div>
          <div className="kpi">
            <div className="lbl">Total memories</div>
            <div className="val">{statsLoading ? '—' : totalMemories.toLocaleString()}</div>
            <div className="delta">across every org</div>
          </div>
          <div className="kpi">
            <div className="lbl">Feedback events</div>
            <div className="val">{statsLoading ? '—' : totalFeedback.toLocaleString()}</div>
            <div className="delta">pack accept / reject</div>
          </div>
        </div>

        {/* Signups sparkline — monochrome, matches home page */}
        <div className="panel" style={{ marginBottom: 16 }}>
          <div className="panel-head">
            <h3>Signups · last {WINDOW_DAYS} days</h3>
            <span className="meta">new accounts per day</span>
          </div>
          <div className="panel-body">
            {signupRows.length === 0 ? (
              <div className="empty" style={{ padding: '40px 0' }}>
                {statsLoading ? 'Loading…' : `No signups in the last ${WINDOW_DAYS} days.`}
              </div>
            ) : (
              <>
                <svg className="chart" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="signupGrad" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="oklch(0.72 0.14 60)" stopOpacity="0.25" />
                      <stop offset="100%" stopColor="oklch(0.72 0.14 60)" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  {[0.25, 0.5, 0.75].map((r, i) => (
                    <line
                      key={i}
                      x1={pad}
                      x2={w - pad}
                      y1={h * r}
                      y2={h * r}
                      stroke="#E3E2DC"
                      strokeDasharray="2 4"
                    />
                  ))}
                  <path d={fillPath} fill="url(#signupGrad)" />
                  <path d={linePath} fill="none" stroke="#0A0A0A" strokeWidth="1.5" />
                  {xs.map((x, i) => (
                    <circle key={i} cx={x} cy={ys[i]} r="2.5" fill="#0A0A0A" />
                  ))}
                </svg>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: 10,
                    fontFamily: 'var(--mono)',
                    fontSize: 10,
                    color: 'var(--ink-4)',
                  }}
                >
                  <span>{formatDay(windowStart)}</span>
                  <span>{formatDay(windowMid)}</span>
                  <span>today</span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="two-col">
          {/* Feature flags — wired to real endpoints */}
          <div className="panel">
            <div className="panel-head">
              <h3>Feature flags</h3>
              <span className="meta">org: {user?.orgId || 'global'}</span>
            </div>
            <div>
              <div className="flag-row">
                <div>
                  <div className="flag-key">PROPAGATION_ON</div>
                  <div className="flag-label">Tag-graph propagation</div>
                  <div className="flag-desc">
                    Allow activation to hop through the tag graph when retrieving packs.
                  </div>
                </div>
                <button
                  type="button"
                  className={`toggle ${propagationOn ? 'on' : ''}`}
                  onClick={() => toggleFeature.mutate({ PROPAGATION_ON: !propagationOn })}
                  disabled={toggleFeature.isPending}
                  aria-pressed={propagationOn}
                  aria-label="PROPAGATION_ON"
                />
              </div>
              <div className="flag-row">
                <div>
                  <div className="flag-key">SLO_GUARD</div>
                  <div className="flag-label">Automatic latency guard</div>
                  <div className="flag-desc">
                    Auto-disable propagation for 60s when p95 breaches 250ms.
                  </div>
                </div>
                <button
                  type="button"
                  className={`toggle ${sloGuard ? 'on' : ''}`}
                  onClick={() => toggleFeature.mutate({ SLO_GUARD: !sloGuard })}
                  disabled={toggleFeature.isPending}
                  aria-pressed={sloGuard}
                  aria-label="SLO_GUARD"
                />
              </div>
              <div className="panel-note">
                Flags apply to the current org. The server re-validates every call — a toggle
                here takes effect on the next request.
              </div>
            </div>
          </div>

          {/* Service health — real values from /admin/metrics. P50/P95/P99
              latency and error rates land here when the histogram + accept
              aggregations ship (P2.1, P2.2). */}
          <div className="panel">
            <div className="panel-head">
              <h3>Service health</h3>
              <span className="meta">live · refresh 15s</span>
            </div>
            <div className="panel-body">
              <div className="slo-status">
                <span
                  className={`slo-dot ${adminMetrics ? 'good' : ''}`}
                />
                <span className="slo-label">
                  {adminMetrics ? 'Operational' : 'Connecting…'}
                </span>
                <span className="slo-meta">
                  {adminMetrics?.uptime != null
                    ? `up ${humanizeUptime(adminMetrics.uptime)}`
                    : '—'}
                </span>
              </div>
              <div className="slo-list">
                <div className="slo-row">
                  <span className="k">Memory</span>
                  <span className="v">
                    {adminMetrics?.memory_usage != null
                      ? `${adminMetrics.memory_usage.toFixed(1)} MB`
                      : '—'}
                  </span>
                  <span className="t">resident</span>
                </div>
                <div className="slo-row">
                  <span className="k">Requests/min</span>
                  <span className="v">
                    {adminMetrics?.requests_per_minute != null
                      ? adminMetrics.requests_per_minute
                      : '—'}
                  </span>
                  <span className="t">current minute</span>
                </div>
                <div className="slo-row">
                  <span className="k">Goroutines</span>
                  <span className="v">
                    {adminMetrics?.goroutines != null
                      ? adminMetrics.goroutines
                      : '—'}
                  </span>
                  <span className="t">live</span>
                </div>
                <div className="slo-row">
                  <span className="k">Uptime</span>
                  <span className="v">
                    {adminMetrics?.uptime != null
                      ? humanizeUptime(adminMetrics.uptime)
                      : '—'}
                  </span>
                  <span className="t">since boot</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tagmaker observability — would have caught yesterday's
            silent model-deprecation outage. Pulls health, queue depth,
            DB connection state from the tagmaker service. */}
        <div className="panel" style={{ marginTop: 16 }}>
          <div className="panel-head">
            <h3>Tagmaker</h3>
            <span className="meta">live · refresh 20s</span>
          </div>
          <div className="panel-body">
            <div className="slo-list">
              <div className="slo-row">
                <span className="k">Service</span>
                <span className="v">
                  {tagmakerHealthErr
                    ? '🔴 unreachable'
                    : tagmakerHealth?.status
                    ? tagmakerHealth.status === 'healthy'
                      ? '🟢 healthy'
                      : `⚠️ ${tagmakerHealth.status}`
                    : '—'}
                </span>
                <span className="t">
                  {tagmakerHealth?.version ? `v${tagmakerHealth.version}` : 'health endpoint'}
                </span>
              </div>
              <div className="slo-row">
                <span className="k">Queue depth</span>
                <span className="v">
                  {tagmakerQueueErr
                    ? 'err'
                    : tagmakerQueue?.queue_depth ?? tagmakerQueue?.depth ?? tagmakerQueue?.size ?? '—'}
                </span>
                <span className="t">pending tag-extract jobs</span>
              </div>
              <div className="slo-row">
                <span className="k">Failed jobs</span>
                <span className="v">
                  {tagmakerQueueErr
                    ? 'err'
                    : tagmakerQueue?.failed ?? tagmakerQueue?.failed_count ?? 0}
                </span>
                <span className="t">last window</span>
              </div>
              <div className="slo-row">
                <span className="k">Database</span>
                <span className="v">
                  {tagmakerDbErr
                    ? '🔴 unreachable'
                    : tagmakerDb?.status === 'connected' || tagmakerDb?.connected
                    ? '🟢 connected'
                    : tagmakerDb?.status || '—'}
                </span>
                <span className="t">
                  {tagmakerDb?.collection_count != null
                    ? `${tagmakerDb.collection_count} collections`
                    : 'mongo'}
                </span>
              </div>
            </div>
            <div
              style={{
                marginTop: 12,
                fontSize: 12,
                color: 'var(--ink-4)',
                lineHeight: 1.55,
              }}
            >
              When tagmaker can&apos;t reach the LLM (e.g. model deprecation, bad
              API key), saves silently land with empty tags. Watch this panel —
              if Service flips red, the silent-failure mode is active.
            </div>
          </div>
        </div>

        {/* Maintenance — destructive ops behind window.confirm. Each
            button calls a real admin endpoint that exists today. */}
        <div className="panel" style={{ marginTop: 16 }}>
          <div className="panel-head">
            <h3>Maintenance</h3>
            <span className="meta">destructive · confirmation required</span>
          </div>
          <div className="panel-body">
            <div style={{ display: 'grid', gap: 14 }}>
              <MaintRow
                title="Cleanup stale memories"
                desc="Delete blocks with TTL expired or status=archived past retention."
                action="Run cleanup"
                pending={cleanup.isPending}
                onClick={() => {
                  if (window.confirm('Delete TTL-expired and stale memories?')) {
                    cleanup.mutate();
                  }
                }}
              />
              <MaintRow
                title="Prune weak edges"
                desc="Remove tag-graph edges below the EMA weight threshold to keep the graph tight."
                action="Prune edges"
                pending={prune.isPending}
                onClick={() => {
                  if (window.confirm('Prune all edges below threshold?')) {
                    prune.mutate();
                  }
                }}
              />
              <MaintRow
                title="Backfill tagsFlat"
                desc="Re-derive the flattened tag array on every memory in this org. Fixes display-only inconsistencies."
                action="Run backfill"
                pending={backfill.isPending}
                onClick={() => {
                  if (window.confirm('Recompute tagsFlat on every memory in this org?')) {
                    backfill.mutate();
                  }
                }}
              />
              <MaintRow
                title="Retry failed tagmaker jobs"
                desc="Re-enqueue any jobs that died in the failed queue (e.g. after a transient LLM outage)."
                action="Retry queue"
                pending={queueRetry.isPending}
                onClick={() => queueRetry.mutate()}
              />
              <MaintRow
                title="Clear failed tagmaker queue"
                desc="Drop the failed queue entirely — only after you've decided not to retry."
                action="Clear queue"
                danger
                pending={queueClear.isPending}
                onClick={() => {
                  if (window.confirm('This permanently drops failed jobs. Continue?')) {
                    queueClear.mutate();
                  }
                }}
              />
              <MaintRow
                title="Force tagmaker rebalance"
                desc="Trigger a manual rebalance of tagmaker workers across the queue. Use when load is uneven."
                action="Rebalance"
                pending={rebalance.isPending}
                onClick={() => rebalance.mutate()}
              />
              <MaintRow
                title="Replay edge learning (24h window)"
                desc="Re-run the EMA edge-learning job over the last 24h of pack events. Adjusts tag-graph edge weights based on accept/reject feedback."
                action="Replay 24h"
                pending={edgeLearn.isPending}
                onClick={() => {
                  if (window.confirm('Replay edge learning over the last 24 hours of pack events? This adjusts tag-graph weights.')) {
                    edgeLearn.mutate();
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Tag economics — real data from /admin/idf-stats */}
        <div className="panel" style={{ marginTop: 16 }}>
          <div className="panel-head">
            <h3>Tag economics</h3>
            <span className="meta">
              {idfStats
                ? `corpus ${idfStats.corpusSize?.toLocaleString() || 0} · IDF gate ${idfStats.minCorpusSize ?? '—'} · α ${idfStats.smoothingAlpha ?? '—'}${idfStats.idfEnabled === false ? ' · IDF DISABLED' : ''}`
                : 'loading…'}
            </span>
          </div>
          <div className="panel-body">
            {!idfStats && (
              <div className="empty" style={{ padding: '20px 0' }}>Loading IDF stats…</div>
            )}
            {idfStats && (!idfStats.tags || idfStats.tags.length === 0) && (
              <div className="empty" style={{ padding: '20px 0' }}>
                {idfStats.corpusSize < (idfStats.minCorpusSize || 50)
                  ? `Corpus below the gate (${idfStats.corpusSize}/${idfStats.minCorpusSize}). IDF is disabled until you cross the threshold; retrieval falls back to uniform tag weighting.`
                  : 'No tag stats yet — save some memories first.'}
              </div>
            )}
            {idfStats?.tags && idfStats.tags.length > 0 && (
              <table className="mtable">
                <thead>
                  <tr>
                    <th>Tag</th>
                    <th style={{ width: 120, textAlign: 'right' }}>docCount</th>
                    <th style={{ width: 120, textAlign: 'right' }}>IDF</th>
                    <th style={{ width: 100 }}>Signal</th>
                  </tr>
                </thead>
                <tbody>
                  {idfStats.tags.map((row) => {
                    const isRare = row.docCount === 1;
                    const isCommon = row.idf < 1;
                    return (
                      <tr key={row.label}>
                        <td><code style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{row.label}</code></td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {row.docCount.toLocaleString()}
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {row.idf.toFixed(3)}
                        </td>
                        <td>
                          {isRare && <span className="tag hot">rare</span>}
                          {isCommon && !isRare && <span className="tag">common</span>}
                          {!isRare && !isCommon && <span className="tag" style={{ opacity: 0.6 }}>mid</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Tenancy — real data from useAuth + sessionStorage */}
        <div className="panel" style={{ marginTop: 16 }}>
          <div className="panel-head">
            <h3>Tenancy</h3>
            <span className="meta">from JWT + session</span>
          </div>
          <div className="panel-body">
            <div className="tenancy-list">
              {tenancy.map((row) => (
                <div key={row.k} className="tenancy-row">
                  <div className="k">{row.k}</div>
                  <div className="v">{row.v}</div>
                </div>
              ))}
            </div>
            <div className="tenancy-note">
              These values are stamped into every JWT issued by the tagging service.
              Requests from a different orgId will be rejected at the edge.
            </div>
          </div>
        </div>

        {/* Users list — lazy, behind a disclosure */}
        <div className="panel" style={{ marginTop: 16 }}>
          <button
            type="button"
            className="disclosure"
            onClick={() => setShowUsers((v) => !v)}
          >
            <div>
              <h3>Users</h3>
              <div className="meta">
                {showUsers ? 'Hide emails' : `Show all ${totalUsers.toLocaleString()} emails`}
              </div>
            </div>
            {showUsers ? <IChevronUp size={16} /> : <IChevronDown size={16} />}
          </button>

          {showUsers && (
            <div className="disclosure-body">
              {usersError && (
                <div className="error-banner" style={{ margin: '0 0 14px' }}>
                  Failed to load users:{' '}
                  {usersError?.response?.status === 403
                    ? 'forbidden (check MME_FOUNDER_EMAILS)'
                    : usersError?.message || 'unknown error'}
                </div>
              )}
              {usersLoading && (
                <div className="empty">
                  <span className="spinner" style={{ marginRight: 8, verticalAlign: 'middle' }} />
                  Loading users…
                </div>
              )}
              {!usersLoading && !usersError && users.length === 0 && (
                <div className="empty">No users yet.</div>
              )}
              {!usersLoading && !usersError && users.length > 0 && (
                <>
                  <table className="mtable">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th style={{ width: 120 }}>Provider</th>
                        <th style={{ width: 120 }}>Signed up</th>
                        <th style={{ width: 120 }}>Last active</th>
                        <th style={{ width: 100 }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.userId}>
                          <td>
                            <span style={{ fontWeight: 500 }}>
                              {u.email || <em style={{ color: 'var(--ink-4)' }}>no email</em>}
                            </span>
                            {u.name && (
                              <span style={{ marginLeft: 8, color: 'var(--ink-4)', fontSize: 12 }}>
                                · {u.name}
                              </span>
                            )}
                            {isFounder(u.email) && (
                              <span className="tag" style={{ marginLeft: 8 }}>
                                founder
                              </span>
                            )}
                          </td>
                          <td>
                            <span className="tag">
                              {u.authProvider === 'google' ? 'Google' : u.authProvider || 'api-key'}
                            </span>
                          </td>
                          <td className="ts">{formatRelativeDate(u.createdAt)}</td>
                          <td className="ts">{formatRelativeDate(u.lastUsedAt)}</td>
                          <td>
                            {u.active ? (
                              <span className="verdict ok">● active</span>
                            ) : (
                              <span className="verdict no">○ inactive</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {userData?.count >= 500 && (
                    <div className="panel-note" style={{ padding: '14px 0 0' }}>
                      Showing first 500 users. Add pagination when you cross this bar.
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <p className="footer-note">
          Allowlist: {FOUNDER_EMAILS.join(', ')}. Managed via the{' '}
          <code>MME_FOUNDER_EMAILS</code> env var on the tagging service.
        </p>
      </div>
    </>
  );
};

export default AdminPage;
